import {
  CreditCard,
  FileText,
  Image as ImageIcon,
  LayoutGrid,
  type LucideIcon,
  Mailbox,
  Megaphone,
  Presentation,
  Shapes,
  Signature,
  Sparkles,
} from 'lucide-react'

import { ARC_END, ARC_START, brandTone } from '@/lib/brand-tones'

/**
 * A glyph and a hue for the things the offer pages sell.
 *
 * Two sets survive here, and what they have in common is that they are the
 * only colour on pages that were otherwise walls of identical dark boxes.
 *
 * `/pl/grafika` was nine grey tiles, each ending in the same uppercase
 * "WYCENA MAILOWA" under a notice that had already said it once. The label is
 * gone; a wizytówka, a logo and a plakat now look like three different things
 * before a word has been read.
 *
 * `/pl/montaz` and `/pl/miniaturki` sell a LADDER, and
 * {@link tierTone} is how that ladder became visible — Starter, Creator and
 * Pro used to be three identical cards you told apart by reading three prices.
 *
 * A third map lived here briefly, keyed by offering slug, and tinted the icon
 * on every tab of the montage switch. The owner took it out: a switch sits
 * above the thing it switches, and five coloured glyphs made it compete with
 * the cards that carry the colour for real. The tabs are plain pills again.
 *
 * Same pattern as `rental-icons.ts` for the equipment grid and `production.ts`
 * for the three specialists, and deliberately a third copy of it rather than
 * one shared abstraction: the sets are keyed by different things, and all they
 * would share is `Record<string, X>` with a fallback.
 *
 * Every colour comes from `lib/brand-tones.ts` — the FLOU gradient's arc and
 * nothing else. Nine options are told apart by lightness and chroma, never by
 * reaching for a hue off the brand.
 */

/* ── QUOTE OPTIONS ─────────────────────────────────────────── */

/** Keyed by `offering_quote_option.slug` — the nine on `/pl/grafika`. */
const QUOTE_ICON: Record<string, LucideIcon> = {
  wizytowka: CreditCard,
  ulotka: FileText,
  logo: Shapes,
  plakat: ImageIcon,
  reklama: Megaphone,
  posty: LayoutGrid,
  prezentacje: Presentation,
  stopki: Signature,
  inne: Sparkles,
}

export function quoteIcon(slug: string): LucideIcon {
  return QUOTE_ICON[slug] ?? Mailbox
}

/**
 * Which stop of the brand palette each option takes.
 *
 * Positions rather than colours, and five of the nine literals this replaces
 * were off the brand arc — an amber, a teal, an orange, a green and a blue.
 * The owner's rule is the whole site: decorative colour comes from the FLOU
 * gradient or it does not come at all. See `lib/brand-tones.ts`.
 *
 * Numbered so that the three-column grid never puts neighbouring stops next
 * to each other, across a row or down a column.
 */
const QUOTE_STOP: Record<string, number> = {
  wizytowka: 0,
  ulotka: 3,
  logo: 6,
  plakat: 4,
  reklama: 7,
  posty: 1,
  prezentacje: 8,
  stopki: 2,
  inne: 5,
}

export function quoteTone(slug: string): string {
  return brandTone(QUOTE_STOP[slug] ?? 0)
}

/* ── THE TIER LADDER ───────────────────────────────────────── */

/**
 * The hue of one rung.
 *
 * Three tiers used to be three identical cards distinguished by a price and by
 * which one carried the "polecany" ring — so the ladder was something you
 * worked out by reading, never something you saw.
 *
 * ## THE BOTTOM RUNG IS NOT ON THE ARC
 *
 * It is grey, and that is the point. The hue used to start at the gradient's
 * violet and walk to its pink, which made the entry level a brand-coloured
 * card too: three lit cards in a row, differing by a hue step most people
 * cannot name, let alone rank. Grey against colour is a difference nobody has
 * to be taught — the cheap level LOOKS like the cheap level, and the ladder
 * starts reading as a ladder from the first glance.
 *
 * Not a neutral grey either: 0.02 of chroma on the brand's own hue keeps it a
 * cousin of the palette rather than a foreign slab. See the note at the top of
 * `brand-tones.ts` about colour that is off the arc.
 *
 * Everything above it walks {@link ARC_START} (violet) to {@link ARC_END}
 * (pink), so on the usual three-rung ladder the middle is violet and the top
 * is the hottest end of the brand. The top rung gets more than a hue — see
 * `isTopTier` and what the card does with it.
 *
 * Derived from the POSITION, not from the slug: the tier slugs differ per
 * offering (`starter`/`creator`/`pro` on montaż, `basic`/`creator`/`pro` on
 * miniaturki) and a fourth level added in the panel has to be coloured by
 * something. With one plan the ladder has no direction to show and the middle
 * of the arc is the honest answer.
 *
 * @param level 1-based rung.
 * @param levels how many rungs this offering has.
 */
export const TIER_BASE_TONE = 'oklch(0.72 0.02 300)'

export function tierTone(level: number, levels: number): string {
  if (levels <= 1) return 'oklch(0.69 0.2 320)'
  // Clamped before it is used, not trusted: `level` comes from an array index
  // and `levels` from its length, and a colour is not the place to find out
  // they stopped agreeing. Out of range used to walk the hue off the arc.
  const rung = Math.min(Math.max(Math.round(level), 1), levels)
  if (rung === 1) return TIER_BASE_TONE
  const t = (rung - 2) / Math.max(levels - 2, 1)
  return `oklch(0.69 0.2 ${Math.round(ARC_START + t * (ARC_END - ARC_START))})`
}

/**
 * Is this the level the offer wants sold?
 *
 * A ladder with one rung has no top — a lone card competing with nothing does
 * not get a crown, it just gets loud.
 */
export function isTopTier(level: number, levels: number): boolean {
  return levels > 1 && level === levels
}
